import { create } from 'zustand';
import { v7 as uuidv7 } from 'uuid';
import { GeoLevel } from '../../types';
import i18next from 'i18next';

export type Message = {
    id: string;
    text: string;
    additionalText?: string;
    details?: string;
    sender: 'client' | 'server';
    indicatorIdsWithCorrelation?: { id: string; correlation: number }[];
    type: 'message' | 'error';
};

type AIStore = {
    shouldShowAIPanel: boolean;
    setShouldShowAIPanel: (value: boolean) => void;
    messages: Message[];
    mode:
        | 'idle'
        | 'correlation-analysis'
        | 'correlation-analysis-done'
        | 'regression-analysis'
        | 'regression-analysis-done';
    startCorrelationAnalysis: (
        yIndicatorId: string,
        geolevel: GeoLevel,
        featureFilter: string[] | null,
        indicatorNames: Record<string, string>
    ) => void;
    startRegressionAnalysis: (
        yIndicatorId: string,
        xIndicatorIds: string[],
        geolevel: GeoLevel,
        featureFilter: string[] | null,
        indicatorNames: Record<string, string>
    ) => void;
    addMessage: (message: Omit<Message, 'id'>) => void;
    updateLastMessageText: (text: string) => void;
    reset: () => void;
    regressionLine: {
        slope: number;
        intercept: number;
        r_squared: number;
    } | null;
    setRegressionLine: (regressionLine: {
        slope: number;
        intercept: number;
        r_squared: number;
    }) => void;
    activeEventSource: EventSource | null;
};

export const useAIStore = create<AIStore>()((set, get) => ({
    shouldShowAIPanel: false,
    setShouldShowAIPanel: (value) => set({ shouldShowAIPanel: value }),
    messages: [],
    mode: 'idle',
    startCorrelationAnalysis: async (
        yIndicatorId: string,
        geolevel: GeoLevel,
        featureFilter: string[] | null,
        indicatorNames: Record<string, string>
    ) => {
        if (get().mode !== 'idle') {
            return;
        }
        const hasFilter = featureFilter && featureFilter.length > 0;

        const { addMessage } = get();
        set({
            mode: 'correlation-analysis',
        });
        addMessage({
            text: i18next.t('ai.starting-correlation'),
            sender: 'client',
            type: 'message',
            additionalText: `
**${i18next.t('ai.target-indicator')}:** ${indicatorNames[yIndicatorId]}

**${i18next.t('ai.geo-level')}:** ${geolevel}

**${i18next.t('ai.filter')}:** ${
    hasFilter ? featureFilter.join(', ') : i18next.t('ai.no-filter')
}
                `,
        });

        let startResponse: Response;
        try {
            startResponse = await fetch(
                `${import.meta.env.VITE_PYAPI_URL}/correlation-analysis`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': i18next.language,
                    },
                    body: JSON.stringify({
                        ycol: yIndicatorId,
                        feature_filter: featureFilter,
                        geolevel: geolevel,
                    }),
                }
            );
        } catch (error) {
            addMessage({
                sender: 'server',
                text: i18next.t('ai.error-generic'),
                type: 'error',
            });
            return;
        }

        let json: any;
        try {
            json = await startResponse.json();
        } catch (error) {
            addMessage({
                sender: 'server',
                text: i18next.t('ai.error-generic'),
                type: 'error',
            });
            return;
        }

        addMessage({ sender: 'server', text: json.message, type: 'message' });
        set({
            mode: 'correlation-analysis-done',
        });

        const importantFeatures = json.important_features;
        const thresholdUsed = json.threshold_used;
        const featureCount = json.feature_count;

        addMessage({
            sender: 'server',
            text: i18next.t('ai.found-indicators', { count: featureCount, threshold: thresholdUsed }),
            indicatorIdsWithCorrelation: importantFeatures,
            type: 'message',
        });
    },
    startRegressionAnalysis: async (
        yIndicatorId: string,
        xIndicatorIds: string[],
        geolevel: GeoLevel,
        featureFilter: string[] | null,
        indicatorNames: Record<string, string>
    ) => {
        if (get().mode !== 'idle') {
            return;
        }

        const { addMessage, setRegressionLine, updateLastMessageText } = get();
        set({
            mode: 'regression-analysis',
        });

        const hasFilter = featureFilter && featureFilter.length > 0;
        addMessage({
            text: i18next.t('ai.starting-regression'),
            additionalText: `
**${i18next.t('ai.target-indicator')}:** ${indicatorNames[yIndicatorId]}

**${i18next.t('ai.explanatory-indicator')}:** ${xIndicatorIds
                .map((id) => indicatorNames[id])
                .join(', ')}

**${i18next.t('ai.geo-level')}:** ${geolevel}

**${i18next.t('ai.filter')}:** ${hasFilter ? featureFilter.join(', ') : i18next.t('ai.no-filter')}
                `,
            sender: 'client',
            type: 'message',
        });

        let startResponse: Response;
        try {
            startResponse = await fetch(
                `${import.meta.env.VITE_PYAPI_URL}/regression-analysis`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': i18next.language,
                    },
                    body: JSON.stringify({
                        yvar: yIndicatorId,
                        xvars: xIndicatorIds,
                        geolevel,
                        feature_filter: featureFilter,
                    }),
                }
            );
            if (!startResponse.ok) {
                addMessage({
                    sender: 'server',
                    text: i18next.t('ai.error-generic'),
                    type: 'error',
                });
                return;
            }
        } catch (error) {
            addMessage({
                sender: 'server',
                text: i18next.t('ai.error-generic'),
                type: 'error',
            });
            return;
        }

        let jsonResponse: any;
        try {
            jsonResponse = await startResponse.json();
        } catch (error) {
            addMessage({
                sender: 'server',
                text: i18next.t('ai.error-generic'),
                type: 'error',
            });
            return;
        }

        const jobId = jsonResponse.job_id;

        setRegressionLine({
            slope: jsonResponse.results.model_parameters.slope,
            intercept: jsonResponse.results.model_parameters.intercept,
            r_squared: jsonResponse.results.model_parameters.r_squared_train,
        });

        // Add an empty message that will be updated with streaming content
        addMessage({
            sender: 'server',
            text: '',
            type: 'message',
        });

        // Use SSE to stream the LLM response
        const eventSource = new EventSource(
            `${import.meta.env.VITE_PYAPI_URL}/regression-analysis/stream/${jobId}?lang=${i18next.language}`,
        );
        set({ activeEventSource: eventSource });

        let streamedText = '';
        let formattedResults = '';

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.error) {
                    eventSource.close();
                    updateLastMessageText(i18next.t('ai.error-generic'));
                    set({ mode: 'regression-analysis-done' });
                    return;
                }

                if (data.done) {
                    eventSource.close();
                    set({ activeEventSource: null });
                    formattedResults = data.formatted_results || formattedResults;

                    // Update the message with the final text including disclaimer
                    const { messages } = get();
                    const lastMessage = messages[messages.length - 1];
                    if (lastMessage) {
                        const finalText = streamedText + `\n\n${i18next.t('ai.r-squared-explanation')}\n_${i18next.t('ai.correlation-disclaimer')}_`;
                        set({
                            messages: messages.map((m, idx) =>
                                idx === messages.length - 1
                                    ? { ...m, text: finalText, details: formattedResults }
                                    : m
                            ),
                            mode: 'regression-analysis-done',
                        });
                    }
                    return;
                }

                if (data.content) {
                    streamedText += data.content;
                    updateLastMessageText(streamedText);
                }
            } catch (e) {
                console.error('Error parsing SSE data:', e);
            }
        };

        eventSource.onerror = () => {
            eventSource.close();
            set({ activeEventSource: null });
            if (streamedText === '') {
                updateLastMessageText(i18next.t('ai.error-generic'));
            }
            set({ mode: 'regression-analysis-done' });
        };
    },
    addMessage: (message) => {
        const { messages } = get();
        set({
            messages: [...messages, { ...message, id: uuidv7() }],
        });
    },
    updateLastMessageText: (text) => {
        const { messages } = get();
        if (messages.length === 0) return;
        set({
            messages: messages.map((m, idx) =>
                idx === messages.length - 1 ? { ...m, text } : m
            ),
        });
    },
    reset: () => {
        get().activeEventSource?.close();
        set({
            mode: 'idle',
            messages: [],
            regressionLine: null,
            activeEventSource: null,
        });
    },
    setRegressionLine: (regressionLine) => {
        set({ regressionLine });
    },
    regressionLine: null,
    activeEventSource: null,
}));
